import { useEffect, useState } from "react";
import { Plus, Loader2, MessageCircle, RefreshCw, Trash2, Pencil, DownloadCloud, Check, UserX, UserCheck, Send, SkipForward, KeyRound, Wallet, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { ConfirmDialog, Modal } from "@/components/Dialogs";
import { DepositAdminModal } from "@/components/DepositPanel";

const storeUrl = (slug) => `${window.location.origin}/toko/${slug}`;

export function waPinLink(r, store) {
  const text = `Halo ${r.name},\n\nPIN reseller Anda untuk *${store.store_name}*:\n*${r.pin}*\n\nBuka link berikut lalu masukkan PIN untuk memesan voucher:\n${storeUrl(store.slug)}\n\nTips: di Chrome pilih menu ⋮ → "Pasang aplikasi" agar mudah dibuka dari layar utama.`;
  return r.phone ? `https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
}

function ResellerForm({ initial, agents = [], onSubmit, onClose }) {
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [ptype, setPtype] = useState(["agen", "konsinyasi", "semi_reseller"].includes(initial?.partner_type) ? initial.partner_type : "reseller");
  const [parent, setParent] = useState(initial?.parent_agent_id || "");
  const [commentAlias, setCommentAlias] = useState(initial?.comment_alias || "");
  const [busy, setBusy] = useState(false);
  const agentOptions = agents.filter((a) => a.id !== initial?.id);
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await onSubmit({ name, phone, comment_alias: commentAlias, partner_type: ptype, parent_agent_id: (ptype === "reseller" || ptype === "konsinyasi" || ptype === "semi_reseller") ? (parent || "") : "" });
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go}>
      <label className="field-label">Nama Reseller / Jalur</label>
      <input className="text-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus data-testid="reseller-form-name" />
      <label className="field-label">Nomor WhatsApp (mis. 6281234567890)</label>
      <input className="text-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="628…" data-testid="reseller-form-phone" />
      <label className="field-label">Alias Comment MikroTik (opsional)</label>
      <input className="text-input" value={commentAlias} onChange={(e) => setCommentAlias(e.target.value)} placeholder="mis. Sari (teks di akhir Comment voucher)" data-testid="reseller-form-comment-alias" />
      <p className="muted-sm">Dipakai mencocokkan Comment voucher di router dengan Mitra ini. Bila kosong, dipakai nama Mitra. Tidak perlu terhubung e-Billing.</p>
      <label className="field-label">Jenis Mitra</label>
      <select className="text-input" value={ptype} onChange={(e) => setPtype(e.target.value)} data-testid="reseller-form-type">
        <option value="agen">Agen</option>
        <option value="reseller">Reseller</option>
        <option value="semi_reseller">Semi Reseller</option>
        <option value="konsinyasi">Konsinyasi</option>
      </select>
      {(ptype === "reseller" || ptype === "konsinyasi" || ptype === "semi_reseller") && (
        <>
          <label className="field-label">Di bawah Agen (opsional)</label>
          <select className="text-input" value={parent} onChange={(e) => setParent(e.target.value)} data-testid="reseller-form-parent-agent">
            <option value="">— Tanpa Agen —</option>
            {agentOptions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <p className="muted-sm">Reseller tetap memakai Harga Reseller. Bila di bawah Agen, Agen memperoleh Komisi saat melayani (harga mitra tidak berubah).</p>
        </>
      )}
      <div className="actions-right">
        <button type="button" className="btn-ghost" onClick={onClose} data-testid="reseller-form-cancel">Batal</button>
        <button type="submit" className="btn-primary" disabled={busy} data-testid="reseller-form-submit">{busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />} Simpan</button>
      </div>
    </form>
  );
}

function EditPinModal({ reseller, q, onDone, onClose }) {
  const [pin, setPin] = useState(reseller?.pin || "");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put(`/admin/resellers/${reseller.id}/pin`, { pin }, q);
      toast.success("PIN Berhasil Diperbarui");
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Modal open={!!reseller} title={`Edit PIN — ${reseller?.name}`} onClose={onClose} testId="edit-pin-modal">
      <form onSubmit={go}>
        <label className="field-label">PIN Baru</label>
        <input className="text-input" value={pin} onChange={(e) => setPin(e.target.value)} required autoFocus data-testid={`edit-pin-input-${reseller?.id}`} />
        <div className="actions-right">
          <button type="button" className="btn-ghost" onClick={onClose}>Batal</button>
          <button type="submit" className="btn-primary" disabled={busy || !pin} data-testid={`edit-pin-save-${reseller?.id}`}>
            {busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />} Simpan PIN
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EbillingImport({ q, onDone, onClose, mitras = [] }) {
  const [creds, setCreds] = useState({ account: "", username: "", password: "" });
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});
  const [link, setLink] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setCreds({ ...creds, [k]: e.target.value });
  const unlinked = mitras.filter((m) => !m.external_id);
  const nLink = Object.values(link).filter(Boolean).length;
  const doLink = async () => {
    const links = Object.entries(link).filter(([, rid]) => rid).map(([i, rid]) => ({ reseller_id: rid, external_id: String(items[i].external_id) }));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resellers/ebilling/link", { ...creds, links }, q);
      if (data.linked.length) toast.success(`${data.linked.length} mitra terhubung: ${data.linked.map((l) => `${l.reseller} ↔ ${l.ebilling}${l.alias ? ` (alias ${l.alias})` : ""}`).join(", ")}`, { duration: 9000 });
      if (data.errors.length) toast.error(data.errors.join("; "), { duration: 9000 });
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const fetchList = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/resellers/ebilling/fetch", creds, q);
      setItems(data.items);
      const s = {}; data.items.forEach((it, i) => { if (!it.already && it.name) s[i] = true; }); setSel(s);
      if (data.items.length === 0) {
        const d = data.diagnostic || {};
        const verdictText = {
          empty_in_ebilling: "e-Billing sendiri melaporkan total 0 untuk akun ini",
          parse_mismatch: "e-Billing melaporkan ada data tetapi baris tidak terbaca",
          empty_unconfirmed: "kosong, tanpa angka total sebagai bukti",
        }[d.verdict] || "-";
        toast.info(`0 reseller dari e-Billing. Kesimpulan: ${verdictText}. Diagnostik: HTTP ${d.status_code ?? "-"}, body ${d.body_type ?? "-"}, key [${(d.top_keys || []).join(", ")}], data ${d.data_type ?? "-"}, total ${d.meta?.total ?? "-"}, percobaan ${(d.attempts || []).length}, baris hal.1: ${d.rows_page1 ?? 0}`, { duration: 12000 });
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const doImport = async () => {
    const chosen = items.filter((_, i) => sel[i]).map((it) => ({ name: it.name, phone: it.phone, external_id: it.external_id || null, ebilling_alias: it.ebilling_alias || null, source: "ebilling" }));
    if (chosen.length === 0) return toast.info("Pilih minimal satu reseller");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/resellers/ebilling/import", { ...creds, items: chosen }, q);
      toast.success(`${data.created} reseller diimport, ${data.skipped} dilewati (sudah ada)${data.alias_updated ? `, ${data.alias_updated} alias e-Billing dilengkapi` : ""}. PIN otomatis dibuat — klik "Kirim WA" untuk mengirim.`);
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggleAll = (v) => { const s = {}; items.forEach((it, i) => { if (!it.already) s[i] = v; }); setSel(s); };

  if (!items) {
    return (
      <form onSubmit={fetchList} data-testid="ebilling-form">
        <p className="dialog-text">Masukkan akun ebilling Anda. Password hanya dipakai saat ini untuk mengambil daftar reseller dan <strong>tidak disimpan</strong>.</p>
        <label className="field-label">Kode Akun / Tenant (account)</label>
        <input className="text-input" value={creds.account} onChange={set("account")} required data-testid="ebilling-account" />
        <label className="field-label">Username</label>
        <input className="text-input" value={creds.username} onChange={set("username")} required data-testid="ebilling-username" />
        <label className="field-label">Password</label>
        <input className="text-input" type="password" value={creds.password} onChange={set("password")} required data-testid="ebilling-password" />
        <div className="actions-right">
          <button type="button" className="btn-ghost" onClick={onClose}>Batal</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="ebilling-fetch">{busy ? <Loader2 className="spin" size={16} /> : <DownloadCloud size={16} />} Ambil Daftar Reseller</button>
        </div>
      </form>
    );
  }
  const n = Object.values(sel).filter(Boolean).length;
  return (
    <div data-testid="ebilling-preview">
      <div className="panel-toolbar">
        <span className="muted-sm">{items.length} reseller ditemukan · {n} dipilih</span>
        <div className="toolbar-right">
          <button className="btn-ghost sm" onClick={() => toggleAll(true)}>Pilih semua</button>
          <button className="btn-ghost sm" onClick={() => toggleAll(false)}>Kosongkan</button>
        </div>
      </div>
      <div className="import-list">
        {items.map((it, i) => (
          <label key={i} className={`import-row ${it.already ? "already" : ""}`} data-testid={`ebilling-item-${i}`}>
            <input type="checkbox" disabled={it.already || !it.name || !!link[i]} checked={!!sel[i] && !link[i]} onChange={(e) => setSel({ ...sel, [i]: e.target.checked })} />
            <span className="import-name">{it.name || <em>(tanpa nama)</em>}</span>
            <span className="muted-sm">{it.phone || "tanpa no. WA"}{it.external_id ? ` · ${it.external_id}` : ""}{it.ebilling_alias ? ` · alias ${it.ebilling_alias}` : ""}</span>
            {it.already && <span className="badge">Sudah ada</span>}
            {!it.already && it.external_id && unlinked.length > 0 && (
              <select className="text-input" style={{ fontSize: 12, padding: "4px 6px", marginTop: 4 }} value={link[i] || ""}
                onChange={(e) => { setLink({ ...link, [i]: e.target.value }); if (e.target.value) setSel({ ...sel, [i]: false }); }} data-testid={`ebilling-link-${i}`}>
                <option value="">— Import sebagai mitra baru —</option>
                {unlinked.filter((m) => m.id === link[i] || !Object.values(link).includes(m.id)).map((m) => <option key={m.id} value={m.id}>Hubungkan ke: {m.name}</option>)}
              </select>
            )}
          </label>
        ))}
      </div>
      <div className="actions-right">
        <button className="btn-ghost" onClick={() => setItems(null)}>Kembali</button>
        {nLink > 0 && <button className="btn-primary" onClick={doLink} disabled={busy} data-testid="ebilling-link-save">{busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />} Hubungkan {nLink} Mitra</button>}
        <button className="btn-primary" onClick={doImport} disabled={busy || n === 0} data-testid="ebilling-import">{busy ? <Loader2 className="spin" size={16} /> : <DownloadCloud size={16} />} Import {n} Reseller</button>
      </div>
    </div>
  );
}

function BulkSend({ queue, store, onSend, onClose }) {
  const [idx, setIdx] = useState(0);
  const [opened, setOpened] = useState(false);
  const cur = queue[idx];
  if (!cur) {
    return (
      <div className="bulk-card" data-testid="bulk-done">
        <p className="dialog-text">Selesai — semua PIN dalam antrean sudah diproses.</p>
        <button className="btn-primary" onClick={onClose} data-testid="bulk-close">Tutup</button>
      </div>
    );
  }
  const open = async () => { window.open(waPinLink(cur, store), "_blank"); setOpened(true); await onSend(cur); };
  const next = () => { setIdx(idx + 1); setOpened(false); };
  return (
    <div className="bulk-card" data-testid="bulk-send">
      <div className="muted-sm" data-testid="bulk-progress-text">{idx + 1} dari {queue.length}</div>
      <div className="bulk-progress"><div style={{ width: `${(idx / queue.length) * 100}%` }} /></div>
      <div className="bulk-name" data-testid="bulk-current-name">{cur.name}</div>
      <div className="muted-sm">{cur.phone ? `WA ${cur.phone}` : "Tanpa nomor WA — pesan dibuka tanpa tujuan"} · PIN <strong>{cur.pin}</strong></div>
      <p className="dialog-text">Klik <strong>Buka WhatsApp</strong>, kirim pesannya, lalu kembali ke sini dan klik <strong>Berikutnya</strong>.</p>
      <div className="actions-right" style={{ justifyContent: "center" }}>
        <button className="btn-ghost" onClick={next} data-testid="bulk-skip"><SkipForward size={14} /> Lewati</button>
        {!opened
          ? <button className="wa-btn" style={{ padding: "10px 18px", fontSize: 14 }} onClick={open} data-testid="bulk-open-wa"><MessageCircle size={15} /> Buka WhatsApp</button>
          : <button className="btn-primary" onClick={next} data-testid="bulk-next"><Send size={15} /> Berikutnya</button>}
      </div>
    </div>
  );
}

function PanelAccessModal({ reseller, q, onClose }) {
  const [defs, setDefs] = useState([]);
  const [val, setVal] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/admin/mitra-panels", q), api.get(`/admin/resellers/${reseller.id}/panels`, q)])
      .then(([d, v]) => { setDefs(d.data); setVal(v.data); }).catch((e) => toast.error(errMsg(e)));
  }, [reseller.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/admin/resellers/${reseller.id}/panels`, val, q); setVal(r.data); toast.success(`Hak akses ${reseller.name} disimpan`); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open title={`Hak Akses Panel — ${reseller.name}`} onClose={onClose} testId="panel-access-modal">
      {!val ? <p className="empty-note">Memuat...</p> : (
        <div style={{ display: "grid", gap: 6 }}>
          {defs.map((d) => (
            <label key={d.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={!!val[d.key]} onChange={(e) => setVal({ ...val, [d.key]: e.target.checked })} data-testid={`panel-check-${d.key}`} style={{ marginTop: 3 }} /> {d.label}
            </label>
          ))}
          <p className="muted-sm">Kredensial MikroTik tidak pernah ditampilkan ke Mitra. Mitra hanya melihat sesi dengan Comment voucher miliknya (alias e-Billing).</p>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn-ghost" onClick={onClose} data-testid="panel-access-cancel">Batal</button>
            <button className="btn-primary" onClick={save} disabled={busy} data-testid="panel-access-save">{busy ? <Loader2 className="spin" size={16} /> : <Check size={16} />} Simpan</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function ResellerManager({ storeId, store }) {
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(null); // {type:'add'|'edit'|'import'|'edit_pin', r?}
  const [target, setTarget] = useState(null); // delete / reset
  const [filter, setFilter] = useState("");
  const q = storeId ? { params: { store_id: storeId } } : {};

  const load = () => api.get("/admin/resellers", q).then((r) => setList(r.data));
  useEffect(() => { load(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (f) => { await api.post("/admin/resellers", f, q); toast.success("Reseller ditambahkan, PIN dibuat otomatis"); await load(); };
  const update = async (f) => { await api.put(`/admin/resellers/${modal.r.id}`, f, q); toast.success("Reseller diperbarui"); await load(); };
  const toggleActive = async (r) => { await api.put(`/admin/resellers/${r.id}`, { active: !r.active }, q); toast.success(r.active ? "Reseller dinonaktifkan" : "Reseller diaktifkan"); await load(); };
  const markSent = async (r) => { await api.post(`/admin/resellers/${r.id}/mark-sent`, {}, q); await load(); };
  const sendWa = async (r) => { window.open(waPinLink(r, store), "_blank"); await markSent(r); };
  const confirmAction = async () => {
    if (target.type === "delete") { await api.delete(`/admin/resellers/${target.r.id}`, q); toast.success("Reseller dihapus"); }
    else { await api.post(`/admin/resellers/${target.r.id}/reset-pin`, {}, q); toast.success("PIN baru dibuat. Kirim ulang lewat WA."); }
    await load();
  };

  if (!list) return <p className="empty-note">Memuat...</p>;
  const rights = store?.access_rights || {};
  const canAddReseller = rights.tambah_reseller !== false;
  const canImport = rights.import_ebiling !== false;
  const canBulk = rights.kirim_semua_belum !== false;
  const canEdit = rights.edit !== false;
  const canDelete = rights.hapus !== false;
  const shown = list.filter((r) => !filter || r.name.toLowerCase().includes(filter.toLowerCase()) || (r.phone && r.phone.includes(filter)) || (r.pin && r.pin.includes(filter)));
  const unsent = list.filter((r) => r.active && !r.pin_sent_at);
  const agents = list.filter((r) => r.partner_type === "agen");
  const agentName = (id) => (list.find((a) => a.id === id) || {}).name;

  return (
    <div data-testid="reseller-manager">
      <div className="panel-toolbar wrap">
        <div>
          <span className="muted-sm" data-testid="reseller-count">{list.length} reseller · {list.filter((r) => !r.pin_sent_at).length} PIN belum dikirim</span>
        </div>
        <div className="toolbar-right">
          <input className="text-input search-input" placeholder="Cari nama / WA / PIN" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="reseller-search" />
          {unsent.length > 0 && (
            <button className="wa-btn" onClick={() => setModal({ type: "bulk", queue: unsent })} disabled={!canBulk} title={!canBulk ? "Dinonaktifkan superadmin" : ""} data-testid="reseller-bulk-open"><Send size={13} /> Kirim Semua yang Belum ({unsent.length})</button>
          )}
          <button className="btn-ghost sm" onClick={() => setModal({ type: "import" })} disabled={!canImport} title={!canImport ? "Dinonaktifkan superadmin" : ""} data-testid="reseller-import-open"><DownloadCloud size={14} /> Import ebilling</button>
          <button className="btn-primary sm" onClick={() => setModal({ type: "add" })} disabled={!canAddReseller} title={!canAddReseller ? "Dinonaktifkan superadmin" : ""} data-testid="reseller-add-open"><Plus size={14} /> Tambah</button>
        </div>
      </div>

      {shown.length === 0 && <p className="empty-note" data-testid="reseller-empty">Belum ada reseller. Tambah manual atau import dari ebilling.</p>}
      <div className="reseller-list">
        {shown.map((r) => (
          <div className={`reseller-card ${r.active ? "" : "inactive"}`} key={r.id} data-testid={`reseller-${r.id}`}>
            <div className="reseller-main">
              <div>
                <strong data-testid={`reseller-name-${r.id}`}>{r.name}</strong>
                <span className="badge" data-testid={`reseller-type-${r.id}`} style={{ textTransform: "capitalize" }}>{{ normal: "Reseller", agen: "Agen", reseller: "Reseller", semi_reseller: "Semi Reseller", konsinyasi: "Konsinyasi" }[r.partner_type] || "Reseller"}</span>
                {r.parent_agent_id && agentName(r.parent_agent_id) && (
                  <span className="badge blue" data-testid={`reseller-parent-${r.id}`}>↳ Agen {agentName(r.parent_agent_id)}</span>
                )}
                {!r.active && <span className="badge gray">Nonaktif</span>}
                {r.external_id
                  ? <span className="badge blue" data-testid={`reseller-sync-${r.id}`}>Terhubung e-Billing{r.ebilling_alias ? ` · ${r.ebilling_alias}` : ""}</span>
                  : <span className="badge gray" data-testid={`reseller-sync-${r.id}`}>Lokal</span>}
                {r.comment_alias && (
                  <span className="badge" title="Alias Comment MikroTik untuk mencocokkan sesi/voucher Mitra ini" data-testid={`reseller-comment-alias-${r.id}`}>Comment: {r.comment_alias}</span>
                )}
                <div className="muted-sm">{r.phone ? `WA ${r.phone}` : "Tanpa no. WA"} · {r.last_login_at ? `Login terakhir ${new Date(r.last_login_at).toLocaleDateString("id-ID")}` : "Belum pernah login"}</div>
                {/* Tombol edit PIN persis di bawah nomor HP sesuai permintaan */}
                <div style={{ marginTop: 4 }}>
                  <button className="btn-ghost sm" style={{ padding: "2px 8px", fontSize: 11 }} onClick={() => setModal({ type: "edit_pin", r })} data-testid={`button-edit-pin-${r.id}`}>
                    <KeyRound size={11} /> Edit PIN
                  </button>
                </div>
              </div>
              <div className="pin-box" data-testid={`reseller-pin-${r.id}`}>
                <span className="pin-label">PIN</span>
                <span className="pin-value">{r.pin}</span>
                <span className={`muted-sm ${r.pin_sent_at ? "sent" : "unsent"}`}>{r.pin_sent_at ? "Terkirim" : "Belum dikirim"}</span>
              </div>
            </div>
            <div className="store-actions">
              <button className="btn-ghost sm" onClick={() => setModal({ type: "panels", r })} data-testid={`reseller-panels-${r.id}`}><ListChecks size={13} /> Hak Akses</button>
              <button className="btn-ghost sm" onClick={() => setModal({ type: "deposit", r })} data-testid={`reseller-deposit-${r.id}`}><Wallet size={13} /> Deposit</button>
              <button className="wa-btn" onClick={() => sendWa(r)} disabled={!canEdit} title={!canEdit ? "Dinonaktifkan superadmin" : ""} data-testid={`reseller-wa-${r.id}`}><MessageCircle size={13} /> Kirim WA</button>
              <button className="btn-ghost sm" onClick={() => setTarget({ type: "reset", r })} data-testid={`reseller-reset-${r.id}`}><RefreshCw size={13} /> PIN Baru</button>
              <button className="btn-ghost sm" onClick={() => setModal({ type: "edit", r })} disabled={!canEdit} title={!canEdit ? "Dinonaktifkan superadmin" : ""} data-testid={`reseller-edit-${r.id}`}><Pencil size={13} /> Edit</button>
              <button className="btn-ghost sm" onClick={() => toggleActive(r)} disabled={!canEdit} title={!canEdit ? "Dinonaktifkan superadmin" : ""} data-testid={`reseller-toggle-${r.id}`}>{r.active ? <><UserX size={13} /> Nonaktifkan</> : <><UserCheck size={13} /> Aktifkan</>}</button>
              <button className="btn-ghost sm danger-text" onClick={() => setTarget({ type: "delete", r })} disabled={!canDelete} title={!canDelete ? "Dinonaktifkan superadmin" : ""} data-testid={`reseller-delete-${r.id}`}><Trash2 size={13} /> Hapus</button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={modal?.type === "add"} title="Tambah Reseller" onClose={() => setModal(null)} testId="reseller-add-modal">
        <ResellerForm agents={agents} onSubmit={create} onClose={() => setModal(null)} />
      </Modal>
      <Modal open={modal?.type === "edit"} title="Edit Reseller" onClose={() => setModal(null)} testId="reseller-edit-modal">
        <ResellerForm initial={modal?.r} agents={agents} onSubmit={update} onClose={() => setModal(null)} />
      </Modal>
      {modal?.type === "panels" && <PanelAccessModal reseller={modal.r} q={q} onClose={() => setModal(null)} />}
      {modal?.type === "edit_pin" && (
        <EditPinModal reseller={modal.r} q={q} onDone={load} onClose={() => setModal(null)} />
      )}
      <Modal open={modal?.type === "bulk"} title="Kirim PIN Massal via WhatsApp" onClose={() => setModal(null)} testId="bulk-modal">
        {modal?.type === "bulk" && <BulkSend queue={modal.queue} store={store} onSend={markSent} onClose={() => setModal(null)} />}
      </Modal>
      <Modal open={modal?.type === "import"} title="Import Reseller dari ebilling" onClose={() => setModal(null)} testId="ebilling-modal">
        <EbillingImport q={q} onDone={load} onClose={() => setModal(null)} mitras={list || []} />
      </Modal>
      <Modal open={modal?.type === "deposit"} title={`Deposit — ${modal?.r?.name || ""}`} onClose={() => setModal(null)} testId="deposit-admin-modal">
        {modal?.type === "deposit" && <DepositAdminModal storeId={storeId} reseller={modal.r} />}
      </Modal>
      <ConfirmDialog
        open={!!target} onClose={() => setTarget(null)}
        title={target?.type === "delete" ? "Hapus Reseller" : "Buat PIN Baru"}
        message={target?.type === "delete"
          ? `Hapus reseller "${target?.r.name}"? Mereka tidak bisa login lagi.`
          : `PIN lama "${target?.r.name}" tidak berlaku lagi dan PIN baru harus dikirim ulang lewat WhatsApp. Lanjutkan?`}
        confirmLabel={target?.type === "delete" ? "Hapus" : "Buat PIN Baru"} danger={target?.type === "delete"}
        onConfirm={confirmAction} testId="reseller-confirm"
      />
    </div>
  );
}
